// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Session and client status (design doc §7.2, §11.5).
 *
 * The session's absolute expiry is shown as a date because it is never extended by use
 * (§7.2): thirty days from sign-in, whatever happens in between. An operator who knows the
 * date can re-authenticate between machines instead of finding out mid-scan.
 *
 * The version line compares this build against the floor the server publishes. A client
 * older than the floor will be refused on writes, and it is better to say so on a page
 * than to let it surface as a rejected filing.
 *
 * The language switch lives here rather than in the navigation rail. It is a setting, not a
 * task: it is chosen once per machine and then remembered (§14), so it belongs with the
 * other facts about this client rather than taking permanent space beside the six things an
 * operator actually navigates to. A switch rather than a dropdown because there are exactly
 * two languages — a two-item select makes the reader open it to find out what the options
 * are, where a switch with both written on it says them at once. English is the default.
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/api/endpoints";
import { ErrorBanner } from "@/components/ErrorBanner";
import { changeLocale } from "@/i18n";

declare const __CLIENT_VERSION__: string;

export function SystemPage() {
  const { t, i18n } = useTranslation();
  const chinese = i18n.language === "zh-CN";
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const policy = useQuery({ queryKey: ["client-policy"], queryFn: api.clientPolicy });

  return (
    <section>
      <h1>{t("system.title")}</h1>
      <ErrorBanner error={me.error ?? policy.error} />
      <table>
        <tbody>
          <tr>
            <th>{t("system.language")}</th>
            <td>
              {/* The two options are written on the switch itself, so it needs no caption:
                  the knob sits under whichever language is in effect. "EN" rather than
                  "英" because the people this switch exists for (US-X-04) are the ones who
                  do not read Chinese. The visible text is decoration; the accessible name
                  says what "on" means, since a switch with no name is announced as nothing. */}
              <button
                type="button"
                role="switch"
                aria-checked={chinese}
                aria-label={t("system.languageSwitch")}
                className="switch"
                onClick={() => changeLocale(chinese ? "en-US" : "zh-CN")}
              >
                <span className="switch-track" aria-hidden="true">
                  <span className="switch-knob" />
                  <span className="switch-option switch-option-off">EN</span>
                  <span className="switch-option switch-option-on">中</span>
                </span>
              </button>
            </td>
          </tr>
          <tr>
            <th>{t("system.account")}</th>
            <td>{me.data?.email ?? "—"}</td>
          </tr>
          <tr>
            <th>{t("system.sessionExpires")}</th>
            <td>
              {me.data ? new Date(me.data.session_expires_at).toLocaleString() : "—"}{" "}
              <span className="hint">{t("system.sessionHint")}</span>
            </td>
          </tr>
          <tr>
            <th>{t("system.clientVersion")}</th>
            <td className="mono">{__CLIENT_VERSION__}</td>
          </tr>
          <tr>
            <th>{t("system.minVersion")}</th>
            <td className="mono">{policy.data?.min_version ?? "—"}</td>
          </tr>
          <tr>
            <th>{t("system.serverTime")}</th>
            <td>{policy.data ? new Date(policy.data.server_time).toLocaleString() : "—"}</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}
